import { useMemo } from 'react';
import { Treemap, Tooltip } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@g17eco/ui-modern/components/ui/card';
import { ChartContainer, type ChartConfig } from '@g17eco/ui-modern/components/ui/chart';
import { useEmissionsFormatter } from '../../hooks/useEmissionsFormatter';
import { getIntensityColor, type TreemapNode } from './intensityTreemapUtils';

interface TreemapMetric {
  label: string;
  unit: string;
  denominatorLabel: string;
}

interface IntensityTreemapProps {
  data: TreemapNode[];
  metric: TreemapMetric;
  headerExtra?: React.ReactNode;
}

const TREEMAP_CONFIG: ChartConfig = {
  intensity: { label: 'Intensity', color: 'hsl(var(--chart-1))' },
};

export const IntensityTreemap = ({ data, metric, headerExtra }: IntensityTreemapProps) => {
  const { formatCompact } = useEmissionsFormatter();

  const { minIntensity, maxIntensity } = useMemo(() => {
    if (data.length === 0) return { minIntensity: 0, maxIntensity: 0 };
    const intensities = data.map((d) => d.intensity);
    return {
      minIntensity: Math.min(...intensities),
      maxIntensity: Math.max(...intensities),
    };
  }, [data]);

  // Recharts Treemap needs data wrapped in a root node for flat type
  const treemapData = useMemo(() => {
    return data.map((node) => ({
      ...node,
      fill: getIntensityColor(node.intensity, minIntensity, maxIntensity),
    }));
  }, [data, minIntensity, maxIntensity]);

  return (
    <Card>
      <CardHeader>
        <div className='tw:items-center tw:justify-between'>
          <CardTitle>Intensity Treemap</CardTitle>
          <div className='tw:text-xs tw:text-muted-foreground tw:mt-1'>
            Box size = {metric.denominatorLabel}. Color = intensity ({metric.unit}). Green = low, red = high.
          </div>
        </div>
        <div className='tw:justify-self-end'>
          {headerExtra}
        </div>
      </CardHeader>
      <CardContent>
        <ChartContainer config={TREEMAP_CONFIG} className='tw:w-full tw:h-[400px]'>
          <Treemap
            data={treemapData}
            dataKey='size'
            nameKey='name'
            type='flat'
            animationDuration={300}
            content={<IntensityTreemapCell x={0} y={0} width={0} height={0} name='' intensity={0} fill='' />}
          >
            <Tooltip content={<IntensityTreemapTooltip metric={metric} formatCompact={formatCompact} />} />
          </Treemap>
        </ChartContainer>
        {/* Color legend */}
        <div className='tw:flex tw:items-center tw:gap-2 tw:mt-3 tw:text-xs tw:text-muted-foreground'>
          <span>Low</span>
          <div
            className='tw:h-3 tw:flex-1 tw:rounded'
            style={{
              background: `linear-gradient(to right, ${getIntensityColor(0, 0, 1)}, ${getIntensityColor(0.5, 0, 1)}, ${getIntensityColor(1, 0, 1)})`,
            }}
          />
          <span>High</span>
          <span className='tw:ml-2 tw:font-medium'>{metric.unit}</span>
        </div>
      </CardContent>
    </Card>
  );
};

// ============================================================================
// Custom Cell Renderer
// ============================================================================

/** Recharts passes layout props + data fields to the content renderer */
interface CellProps {
  x: number;
  y: number;
  width: number;
  height: number;
  name: string;
  intensity: number;
  fill: string;
}

const IntensityTreemapCell = (props: CellProps) => {
  const { x, y, width, height, name, intensity, fill } = props;
  if (!name || !fill) return null;
  const showLabel = width > 50 && height > 28;
  const showIntensity = width > 50 && height > 42;

  return (
    <g>
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        fill={fill}
        stroke='var(--border)'
        strokeWidth={1}
        rx={2}
      />
      {showLabel && (
        <text
          x={x + width / 2}
          y={y + height / 2 - (showIntensity ? 6 : 0)}
          textAnchor='middle'
          dominantBaseline='middle'
          className='tw:fill-foreground'
          fontSize={11}
          fontWeight={600}
        >
          {name.length > width / 7 ? `${name.slice(0, Math.floor(width / 7))}…` : name}
        </text>
      )}
      {showIntensity && (
        <text
          x={x + width / 2}
          y={y + height / 2 + 10}
          textAnchor='middle'
          dominantBaseline='middle'
          className='tw:fill-foreground'
          fontSize={10}
          opacity={0.7}
        >
          {intensity.toFixed(1)}
        </text>
      )}
    </g>
  );
};

// ============================================================================
// Custom Tooltip
// ============================================================================

interface TooltipProps {
  active?: boolean;
  payload?: Array<{ payload: TreemapNode & { fill: string } }>;
  metric: TreemapMetric;
  formatCompact: (value: number | null | undefined) => { display: string };
}

const IntensityTreemapTooltip = ({ active, payload, metric, formatCompact }: TooltipProps) => {
  if (!active || !payload?.length) return null;
  const node = payload[0].payload;

  return (
    <div className='tw:bg-background tw:border tw:rounded-lg tw:shadow-lg tw:p-3 tw:text-sm'>
      <p className='tw:font-semibold tw:mb-1'>{node.name}</p>
      <div className='tw:space-y-0.5 tw:text-xs'>
        <div>Total CO₂e: <span>{formatCompact(node.totalCo2e).display}</span></div>
        <div>{metric.label}: <span>{node.denominatorValue.toLocaleString()} {metric.denominatorLabel}</span></div>
        <div>Intensity: <span className='tw:font-semibold'>{node.intensity.toFixed(2)} {metric.unit}</span></div>
      </div>
    </div>
  );
};
